import React from 'react';
import { Button as AntButton } from 'antd';
import styled from 'styled-components';
import { colorsPalette } from '../../styles/colors-palette';
import { Skeleton } from './Skeleton';

type ButtonSize = 'large' | 'small' | 'middle' | 'compact';
type ButtonType = 'primary' | 'default' | 'link' | 'text' | 'dashed';
type ButtonIconPosition = 'start' | 'end';

interface CustomStyles {
  paddingLeft?: CSSSize | 0;
  paddingRight?: CSSSize | 0;
  padding?: string | 0;
  backgroundColor?: Color;
  borderColor?: Color;
  color?: Color;
  hoverBackgroundColor?: Color;
  hoverBorderColor?: Color;
  hoverColor?: Color;
  width?: CSSSize;
  height?: CSSSize;
  whiteSpace?: string;
  marginTop?: CSSSize;
  fontWeight?: string | number;
  fontSize?: CSSSize;
  disabledOpacity?: number;
  alignSelf?: React.CSSProperties['alignSelf'];
  disabledBackgroundColor?: Color;
  disabledBorderColor?: Color;
  disabledColor?: Color;
  borderRadius?: React.CSSProperties['borderRadius'];
}

interface ButtonProps {
  size?: ButtonSize;
  type?: ButtonType;
  onClick?: () => void;
  disabled?: boolean;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  danger?: boolean;
  className?: string;
  loading?: boolean;
  id?: string;
  skeletonWidth?: CSSSize;
  customStyles?: CustomStyles;
  iconPosition?: ButtonIconPosition;
  testIds?: { main?: string };
}

function Button({
  onClick,
  size,
  type,
  disabled,
  icon,
  danger,
  children,
  className,
  loading,
  skeletonWidth,
  id,
  customStyles,
  iconPosition,
  testIds,
}: ButtonProps): React.ReactElement {
  if (loading) {
    return <StyledSkeleton width={skeletonWidth} className={className} />;
  }

  const antSize: 'large' | 'middle' | 'small' =
    size === 'compact' ? 'middle' : size !== undefined ? size : 'large';

  return (
    <StyledButton
      className={className}
      onClick={onClick}
      size={antSize}
      type={type}
      id={id}
      disabled={disabled}
      icon={icon}
      danger={danger}
      customstyles={customStyles}
      iconPosition={iconPosition ? iconPosition : 'start'}
      data-testid={testIds?.main}
      compact={size === 'compact'}
    >
      {children}
    </StyledButton>
  );
}
// TODO remake with theme props (like Menu)
const StyledButton = styled(AntButton).withConfig({
  shouldForwardProp: prop => (prop as string) !== 'compact',
})<{
  customstyles?: CustomStyles;
  compact?: boolean;
}>`
  &.ant-btn {
    align-self: ${props => props.customstyles?.alignSelf};
    width: ${props => props.customstyles?.width || 'unknown'};
    height: ${props => props.customstyles?.height || 'unknown'};
    white-space: ${props => props.customstyles?.whiteSpace};
    margin-top: ${props => props.customstyles?.marginTop};
    margin-top: ${props => props.customstyles?.paddingLeft};
    font-weight: ${props => props.customstyles?.fontWeight};
    font-size: ${props => props.customstyles?.fontSize};
    padding: ${props => props.customstyles?.padding};
    border-radius: ${props => props.customstyles?.borderRadius};
  }

  &.ant-btn:disabled {
    ${props =>
      props.customstyles?.disabledOpacity != null &&
      `opacity: ${props.customstyles.disabledOpacity};`}
    color: ${props => props.customstyles?.disabledColor};
    background-color: ${props => props.customstyles?.disabledBackgroundColor};
    border-color: ${props => props.customstyles?.disabledBorderColor};
  }
  &.ant-btn:not(:disabled):focus-visible {
    outline: none;
    transition: none;
    outline-offset: 0;
  }

  &.ant-btn-dashed {
    &:not(.disabled) {
      &:hover {
        color: ${colorsPalette.dustyBlue500};
        border-color: ${colorsPalette.dustyBlue500};
      }
    }
  }

  &.ant-btn-primary,
  &.ant-btn-default,
  &.ant-btn-dashed {
    box-shadow: none;
  }

  &.ant-btn-default {
    background-color: ${props => props.customstyles?.backgroundColor};
    ${props => props.customstyles?.color != null && `color: ${props.customstyles.color};`}
    ${props =>
      (props.customstyles?.borderColor != null || props.customstyles?.backgroundColor != null) &&
      `border-color: ${props.customstyles.borderColor ?? props.customstyles.backgroundColor};`}

    &.ant-btn-dangerous {
      color: ${colorsPalette.red500};
      border-color: ${colorsPalette.red500};
    }

    ${props =>
      props.customstyles?.backgroundColor &&
      props.customstyles?.color &&
      `
      &&:hover:not(:disabled),
      &&:focus-visible:not(:disabled) {
        background-color: ${
          props.customstyles.hoverBackgroundColor ?? props.customstyles.backgroundColor
        };
        border-color: ${
          props.customstyles.hoverBorderColor ??
          props.customstyles.hoverBackgroundColor ??
          props.customstyles.borderColor ??
          props.customstyles.backgroundColor
        };
        color: ${props.customstyles.hoverColor ?? props.customstyles.color};
      }
    `}
  }

  &.ant-btn-link {
    color: ${props => props.customstyles?.color || `${colorsPalette.dustyBlue500}`};
  }

  &.ant-btn.ant-btn-lg {
    padding-left: ${props => props.customstyles?.paddingLeft};
    padding-right: ${props => props.customstyles?.paddingRight};
  }

  &.ant-btn.ant-btn-sm {
    padding-left: ${props => props.customstyles?.paddingLeft};
    padding-right: ${props => props.customstyles?.paddingRight};
  }

  ${props =>
    props.compact &&
    `
      &.ant-btn {
        height: 32px !important;
        min-height: 32px !important;
        padding: 4px 15px !important;
        font-size: 14px !important;
        line-height: 22px !important;
      }
    `}
`;

const StyledSkeleton = styled(Skeleton)`
  &.ant-skeleton.ant-skeleton-element .ant-skeleton-input {
    height: 40px;
    vertical-align: middle;
    border-radius: 8px;
  }
`;

export default Button;
export type { ButtonProps, ButtonSize };
